import { useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { FolderOpenIcon, TrashIcon } from "@heroicons/react/24/outline";
import AppButton from "../buttons/AppButton";
import IconButton from "../buttons/IconButton";
import StatusMessage from "../feedback/StatusMessage";
import { classNames } from "../styles/classNames";
import type { FieldControlProps } from "./Field";
import { pickedFromDrop, pickedFromInput, type PickedFiles } from "./pickedFiles";

interface FileDropzoneProps {
  /** From Field, so its label names the drop zone. */
  control: FieldControlProps;
  picked: PickedFiles | null;
  onPick: (picked: PickedFiles | null) => void;
  /** What may be dropped, such as the formats; under the prompt. */
  hint?: string;
  disabled?: boolean;
}

/**
 * Choose files or a folder by clicking or dropping, as v1's import dialog did
 * (`legacy/dmw/datamodel-workflow/frontend/src/components/ontology/OntologyImportDialog.tsx`).
 * What was chosen shows as one line with its file count and a button to remove it.
 */
export default function FileDropzone({ control, picked, onPick, hint, disabled = false }: FileDropzoneProps) {
  const { t } = useTranslation();
  const filesInput = useRef<HTMLInputElement | null>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  if (picked !== null) {
    return (
      <StatusMessage tone="success">
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 break-all font-medium" role="status">
            {t("fileDropzone.chosen", { name: picked.name ?? t("fileDropzone.severalFiles"), count: picked.files.length })}
          </span>
          <IconButton label={t("fileDropzone.remove")} disabled={disabled} onClick={() => { onPick(null); }} className="app-action-link-danger p-1">
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </div>
      </StatusMessage>
    );
  }
  const drop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    void pickedFromDrop(event.dataTransfer).then(onPick);
  };
  return (
    <div className="space-y-2">
      <button
        {...control}
        type="button"
        disabled={disabled}
        className={classNames("app-dropzone block w-full cursor-pointer p-6 text-center", dragging && "app-dropzone-active")}
        onClick={() => filesInput.current?.click()}
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => { setDragging(false); }}
        onDrop={drop}
      >
        <span className="app-text-muted block text-sm">{t("fileDropzone.prompt")}</span>
        {hint === undefined ? null : <span className="app-help-text mt-1 block">{hint}</span>}
      </button>
      <AppButton size="xs" disabled={disabled} onClick={() => folderInput.current?.click()}>
        <FolderOpenIcon className="mr-1 h-4 w-4" aria-hidden="true" />
        {t("fileDropzone.chooseFolder")}
      </AppButton>
      <input ref={filesInput} type="file" multiple hidden tabIndex={-1} onChange={(event) => { onPick(pickedFromInput(event.target.files)); }} />
      <input
        ref={(element) => { element?.setAttribute("webkitdirectory", ""); folderInput.current = element; }}
        type="file"
        hidden
        tabIndex={-1}
        onChange={(event) => { onPick(pickedFromInput(event.target.files)); }}
      />
    </div>
  );
}
